// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { InvoiceEditFormData } from '@/billing/schemas/invoice-edit-form-schema';
import { FormTextField } from '@/components/form-text-field';
import { Customer } from '@/customer/api/types';
import { CustomerAutocomplete } from '@/customer/components/customer-autocomplete';
import { useProjectBillingInfo } from '@/project/hooks/use-project-billing-info';
import { Divider, Stack, Typography } from '@mui/material';
import { useCallback } from 'react';
import { Control, Controller, UseFormSetValue } from 'react-hook-form';

interface InvoiceEditFormBillToSectionProps {
  control: Control<InvoiceEditFormData>;
  setValue: UseFormSetValue<InvoiceEditFormData>;
  customers: Customer[];
  projectKey: string | null;
  isDisabled?: boolean;
}

/**
 * Col 2 of the invoice edit form: customer picker + Bill to fields (company
 * name, address) + Billing contact fields (care of, phone, email).
 *
 * Suspends on {@link useProjectBillingInfo}. On customer change, auto-fills
 * bill-to fields with coalesced values `project.field ?? customer.field` to
 * match the backend sweep rule in `sweep.go`. Users can still manually edit
 * any field after the auto-fill.
 */
export function InvoiceEditFormBillToSection(
  props: InvoiceEditFormBillToSectionProps
) {
  // Destructure setValue in the body so the useCallback dep is the stable
  // react-hook-form setter, not the per-render props object reference.
  const { setValue } = props;
  const projectBilling = useProjectBillingInfo(props.projectKey);

  const handleCustomerChange = useCallback(
    (
      customer: Customer | null,
      fieldOnChange: (value: Customer | null) => void
    ) => {
      fieldOnChange(customer);
      const customerContact = customer?.billingContact ?? null;

      setValue('billToCompanyName', customer?.name ?? '', {
        shouldDirty: true,
      });
      setValue(
        'billToAddress',
        projectBilling?.billingAddress ?? customer?.billingAddress ?? '',
        { shouldDirty: true }
      );
      setValue(
        'billingContactName',
        projectBilling?.billingContactName ?? customerContact?.name ?? '',
        { shouldDirty: true }
      );
      setValue(
        'billToEmail',
        projectBilling?.billingContactEmail ?? customerContact?.email ?? '',
        { shouldDirty: true }
      );
      setValue(
        'billToPhone',
        projectBilling?.billingContactPhone ?? customerContact?.phone ?? '',
        { shouldDirty: true }
      );
    },
    [projectBilling, setValue]
  );

  return (
    <Stack spacing={3}>
      <Stack spacing={2}>
        <Typography variant="subtitle2">Bill to</Typography>
        <Controller
          name="customer"
          control={props.control}
          render={({ field }) => (
            <CustomerAutocomplete<Customer, false>
              size="small"
              label="Customer"
              options={props.customers}
              value={field.value}
              onChange={(_, value) =>
                handleCustomerChange(value, field.onChange)
              }
              disabled={props.isDisabled}
            />
          )}
        />
        <FormTextField
          name="billToCompanyName"
          control={props.control}
          label="Company name"
          fullWidth
          disabled={props.isDisabled}
        />
        <FormTextField
          name="billToAddress"
          control={props.control}
          label="Address"
          fullWidth
          isMultiline
          minimumRows={3}
          disabled={props.isDisabled}
        />
      </Stack>

      <Divider />

      <Stack spacing={2}>
        <Typography variant="subtitle2">Billing contact</Typography>
        <FormTextField
          name="billingContactName"
          control={props.control}
          label="Name"
          fullWidth
          disabled={props.isDisabled}
        />
        <FormTextField
          name="billToPhone"
          control={props.control}
          label="Phone"
          fullWidth
          disabled={props.isDisabled}
        />
        <FormTextField
          name="billToEmail"
          control={props.control}
          label="Email"
          fullWidth
          disabled={props.isDisabled}
        />
      </Stack>
    </Stack>
  );
}
